import H from 'constants/httpMethod.const';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromArrayToCommatose from 'utils/converters/string/fromArrayToCommatose.util';
import { fromAnyDateToZuluDayBgn, fromAnyDateToZuluDayEnd } from 'utils/converters/time/from.util';
import logger from 'utils/logger.util';
import actionEndpointBe from 'utils/mw/actionEndpointBe.util';
import { isNotValidId } from 'utils/predicates/fields.util';

const L = logger('beTransfersReport'); // eslint-disable-line no-unused-vars

const fromDtoToRaw = void 1; // for printing in console, you can use: L.wrap('fromDtoToRaw()', alwaysSame);

const fromDtoToTransfersCommission = (dto) => ({
  item: dto?.item,
  cardAmount: dto?.card_amount,
  totalServiceAmount: dto?.total_service_amount,
  stylist: dto?.stylist,
  stylistId: dto?.stylist_id,
  businessCommission: dto?.business_commission,
  businessRevenue: dto?.business_revenue,
  commissionType: dto?.commission_type,
  commissionCategory: dto?.commission_category,
  stylistPercentage: dto?.stylist_percentage,
  stylistRevenue: dto?.stylist_revenue,
});

const fromDtoToTransfersCommissions = (dto) => asArray(dto?.results).map(fromDtoToTransfersCommission);

// SEE: https://getslick.atlassian.net/browse/GS-13449

// core.api: GET payments/stripe/transfers/report/all/:salonId/  → replaces old: payments/stripe/transfers/report/:salonId/
// core.api: GET payments/stripe/transfers/report/personal/:salonId/ -> replaces old: payments/stripe/transfers/report/:salonId/

const MISSING = 'Missing one or more of the required parameters';

const reconfigureGroup = (config) => (options, extra) => {
  const bgn = fromAnyDateToZuluDayBgn(extra?.bgn) || fromAnyDateToZuluDayBgn(extra?.begin) || null;
  const end = fromAnyDateToZuluDayEnd(extra?.end) || null;
  const ids = fromArrayToCommatose(extra?.ids) || null;
  const group = extra?.group;
  const perm = extra?.all ? 'all' : 'personal';

  const params = { bgn, end, ids, group, perm };

  if (!bgn || !end || !perm) {
    L.warn('reconfigureGroup()', options?.name, MISSING, params, 'for', { config, options, extra });
  }

  return {
    ...options,
    ...config,
    fromDto: fromDtoToRaw,
    params,
  };
};

const reconfigureTransfers = (config) => (options, extra) => {
  const bgn = fromAnyDateToZuluDayBgn(extra?.bgn) || fromAnyDateToZuluDayBgn(extra?.begin) || null;
  const end = fromAnyDateToZuluDayEnd(extra?.end) || null;
  const ids = fromArrayToCommatose(extra?.ids) || null;
  const intentIds = fromArrayToCommatose(extra?.intentIds) || null;
  const perm = extra?.all ? 'all' : 'personal';

  const params = { bgn, end, ids, perm, intentIds };

  if (!bgn || !end || !perm || !ids || !intentIds) {
    L.warn('reconfigureTransfers()', options?.name, MISSING, params, 'for', { config, options, extra });
  }

  return {
    ...options,
    ...config,
    fromDto: fromDtoToRaw,
    params,
  };
};

export const ibeTransfersCommissionRead = actionEndpointBe(
  'ibeTransfersCommissionRead',
  H.get,
  'payments/stripe/retrieve/transfer/commission/:salonId/:intentId/',
  (options, extra) => ({
    ...options,
    inert: true,
    isMissingParams: isNotValidId(extra?.intentId),
    params: { intentId: fromAnyToTrimmed(extra?.intentId) || 0 },
    fromDto: fromDtoToTransfersCommissions,
  }),
);

export const ibeTransfersReportRead = actionEndpointBe(
  'ibeTransfersReportRead',
  H.get,
  'payments/stripe/transfers/report/:perm/:salonId/?end=:end&start=:bgn&ids=:ids&intent_ids=:intentIds',
  reconfigureTransfers({ inert: true }),
);

export const abeTransfersReportRead = actionEndpointBe(
  'abeTransfersReportRead',
  H.get,
  'payments/stripe/transfers/report/:perm/:salonId/?end=:end&start=:bgn&ids=:ids&intent_ids=:intentIds',
  reconfigureTransfers({ inert: false }),
);

export const ibeTransfersReportGroupRead = actionEndpointBe(
  'ibeTransfersReportGroupRead',
  H.get,
  'payments/stripe/transfers/report/:perm/:salonId/group/?end=:end&start=:bgn&stylist_id=:ids?&staff=:group?',
  reconfigureGroup({ inert: true }),
);

export const abeTransfersReportGroupRead = actionEndpointBe(
  'abeTransfersReportGroupRead',
  H.get,
  'payments/stripe/transfers/report/:perm/:salonId/group/?end=:end&start=:bgn&stylist_id=:ids?&staff=:group?',
  reconfigureGroup({ inert: false }),
);
